import { lazy, Suspense, useEffect, useState } from 'react';
import { hardReload } from '@/lib/hard-reload';
import type { ComponentProps } from 'react';
// Type-only import — erased at build time, so it creates NO static dependency
// on the CMCApp chunk (that's the whole point of this wrapper).
import type { CMCApp as CMCAppComponent } from './CMCApp';

const CMCAppInner = lazy(() =>
  import('./CMCApp').then((m) => ({ default: m.CMCApp })));

// Brief branded loader while the CMCApp chunk downloads (a fraction of a second,
// instant on repeat visits once cached).
/**
 * Brief branded loader while the CMCApp chunk downloads.
 *
 * After ~10s it stops pretending. If the dynamic import never settles — a
 * dropped connection mid-download, or a page that was loaded before a deploy
 * and is now asking for a chunk graph that has moved — React's Suspense has no
 * timeout of its own, so the spinner would otherwise turn forever with no way
 * out. Reported from production 2026-08-21: finishing the demo school wizard
 * left a permanent spinner. An escape hatch beats a silent hang whatever the
 * root cause turns out to be.
 */
const Fallback = () => {
  const [stalled, setStalled] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setStalled(true), 10000);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-5 bg-[#1e2d5a] px-6 text-center">
      <div className="w-10 h-10 rounded-full border-[3px] border-white/25 border-t-white animate-spin" />
      {stalled && (
        <>
          <p className="text-white/80 text-sm max-w-xs leading-relaxed">
            This is taking longer than it should. Reloading usually fixes it. Your setup is saved.
          </p>
          <button
            type="button"
            onClick={() => { void hardReload(); }}
            className="px-4 py-2 rounded-lg bg-white text-[#1e2d5a] text-sm font-semibold hover:brightness-95 transition-all"
          >
            Reload
          </button>
        </>
      )}
    </div>
  );
};

/**
 * CMCApp behind a lazy boundary. Every entry point (Index, TeacherPage,
 * DemoApp) imports the app from HERE rather than from ./CMCApp directly, so the
 * ~500 KB monolith lands in its own chunk and is downloaded on demand instead
 * of being parsed as part of each route's initial bundle. Drop-in: same name,
 * same props as the real CMCApp.
 */
export function CMCApp(props: ComponentProps<typeof CMCAppComponent>) {
  return (
    <Suspense fallback={<Fallback />}>
      <CMCAppInner {...props} />
    </Suspense>
  );
}
